import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from "../common/reducers/utilities";
import { ensureInteger } from "../common/utils/generalUtils";
import { ensureArray } from "../common/utils/arrayUtilities";

const comapreComments = (c1, c2) => ensureInteger(c1.id) === ensureInteger(c2.id);
const compareFields = (f1, f2) => ensureInteger(f1.id) === ensureInteger(f2.id);

export const requiredCommentSlice = createSlice({
    name: 'requiredComments',
    initialState: {
        defs: [],
        fields: {}
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = ensureArray(action.payload);
        },
        addOrUpdateDef: (state, action) => {
            for (const c of ensureArray(action.payload)) {
                let idx = state.defs.findIndex(c2 => comapreComments(c2, c));
                if (idx >= 0) {
                    state.defs.splice(idx, 1, c)
                } else {
                    state.defs.push(c);
                }
            }
        },
        removeDef: (state, action) => {
            for (const c of ensureArray(action.payload)) {
                let idx = state.defs.findIndex(c2 => comapreComments(c2, c));
                if (idx >= 0) {
                    state.defs.splice(idx, 1);
                }
            }
        },
        addOrUpdateField: (state, action) => {
            for (const f of ensureArray(action.payload)) {
                const commentIdx = state.defs.findIndex(c => c.id === f.commentid);
                if (commentIdx > -1) {
                    const comment = JSON.parse(JSON.stringify(state.defs[commentIdx]));
                    const idx = comment.fields?.findIndex(f2 => compareFields(f, f2));
                    if (idx >= 0) {
                        comment.fields.splice(idx, 1, f);
                    } else {
                        comment.fields.push(f);
                    }
                    state.defs.splice(commentIdx, 1, comment);
                }
            }
        },
        removeField: (state, action) => {
            for (const f of ensureArray(action.payload)) {
                const commentIdx = state.defs.findIndex(c => c.id === f.commentid);
                if (commentIdx > -1) {
                    const comment = JSON.parse(JSON.stringify(state.defs[commentIdx]));
                    const idx = comment.fields?.findIndex(f2 => compareFields(f, f2));
                    if (idx >= 0) {
                        comment.fields.splice(idx, 1);
                        state.defs.splice(commentIdx, 1, comment);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => requiredCommentSlice.getInitialState());
    }
});

// #region network operations
const fetchRequiredComments = network => network.post('/api/v1/requiredComments', {}, requiredCommentSlice.actions.setDefs);

export const insertRequiredComment = (network, comment) => network.post('/api/v1/requiredComment', comment, requiredCommentSlice.actions.addOrUpdateDef);
export const updateRequiredComment = (network, comment) => network.put('/api/v1/requiredComment', comment, requiredCommentSlice.actions.addOrUpdateDef);
export const removeRequiredComment = (network, comment) => network.delete('/api/v1/requiredComment/' + encodeURIComponent(comment.id), requiredCommentSlice.actions.removeDef);

export const insertCommentField = (network, field) => network.post('/api/v1/requiredCommentField', field, requiredCommentSlice.actions.addOrUpdateField);
export const updateCommentField = (network, field) => network.put('/api/v1/requiredCommentField', field, requiredCommentSlice.actions.addOrUpdateField);
export const remvoeCommentField = (network, field) => network.delete('/api/v1/requiredCommentField/' + encodeURIComponent(field.id), requiredCommentSlice.actions.removeField);
// #endregion

// #region selectors
const selectAllComments = state => state.requiredComments.defs;
export const matchComment = (comment, site, level, type, problem, event) => comment.applications.some(a => (!a.site || a.site === site) && (!a.level || a.level === level) && (!a.type || a.type === type) && (!a.event || a.event === event) && (a.mode === 'Dispatch') && (!a.problem || a.problem.toLowerCase().startsWith(problem?.toLowerCase())));
const selectComment = (site, level, type, problem, event) => createSelector([selectAllComments], comments => {
    return comments.filter(comment => matchComment(comment, site, level, type, problem, event));
});
// #endregion

// #region listeners
const listen = {
    requiredCommentCreated: (comment, { dispatch }) => dispatch(requiredCommentSlice.actions.addOrUpdateDef(comment)),
    requiredCommentUpdated: (comment, { dispatch }) => dispatch(requiredCommentSlice.actions.addOrUpdateDef(comment)),
    requiredCommentRemoved: (comment, { dispatch }) => dispatch(requiredCommentSlice.actions.removeDef(comment)),
    requiredCommentFieldCreated: (field, { dispatch }) => dispatch(requiredCommentSlice.actions.addOrUpdateField(field)),
    requiredCommentFieldUpdated: (field, { dispatch }) => dispatch(requiredCommentSlice.actions.addOrUpdateField(field)),
    requiredCommentFieldRemoved: (field, { dispatch }) => dispatch(requiredCommentSlice.actions.removeField(field))
}
// #endregion

// #region permission checks
export const canInsertRequiredcomment = (credentials, comment) => credentials.access.hasFull('administrator');
export const canUpdateRequiredcomment = (credentials, comment) => credentials.access.hasFull('administrator');
export const canRemoveRequiredcomment = (credentials, comment) => credentials.access.hasFull('administrator');

export const canInsertField = (credentials, field) => credentials.access.hasFull('administrator');
export const canUpdateField = (credentials, field) => credentials.access.hasFull('administrator');
export const canRemoveField = (credentials, field) => credentials.access.hasFull('administrator');
// #endregion

export const [useAllRequiredComments] = dataHookFactory(selectAllComments, { run: fetchRequiredComments, listen });
export const [useRequiredComments] = dataHookFactory(selectComment, () => ({ run: fetchRequiredComments, listen }));

const reducerDetails = { name: requiredCommentSlice.name, reducer: requiredCommentSlice.reducer };
export default reducerDetails;